import { apiMiddleware } from './apiMiddleware'

// TypeScript interfaces for the Firm model
export interface Firm {
  id: string  // Changed from number to string for UUID
  first_name: string
  last_name: string
  name: string
  email: string
  law_firm: string
  state: string
  created_at?: string
  updated_at?: string
}

export interface CreateFirmData {
  first_name: string
  last_name: string
  email: string
  law_firm: string
  state: string
}

export interface FirmsResponse {
  firms: Firm[]
  total_count: number
  page: number
  page_size: number
  total_pages: number
}

export interface FirmDetailResponse {
  firm: Firm
}

export interface ApiError {
  error: boolean
  errors?: any
  message?: string
}

export interface ApiSuccess {
  error: boolean
  message: string
}

export interface EmailAvailabilityResponse {
  available: boolean
  message: string
}

class FirmsApiService {
  // No need for baseUrl or makeRequest - using middleware

  // Get all firms with search and pagination
  async getFirms(page: number = 1, pageSize: number = 50, search: string = ''): Promise<FirmsResponse> {
    const params = new URLSearchParams()
    if (page > 1) params.append('page', page.toString())
    if (pageSize !== 50) params.append('page_size', pageSize.toString())
    if (search) params.append('search', search)
    
    const queryString = params.toString()
    const endpoint = queryString ? `firms/?${queryString}` : 'firms/'
    
    return apiMiddleware.get<FirmsResponse>(endpoint)
  }

  // Get a specific firm
  async getFirm(id: string): Promise<FirmDetailResponse> {
    return apiMiddleware.get<FirmDetailResponse>(`firms/${id}/`)
  }

  // Create a new firm
  async createFirm(data: CreateFirmData): Promise<ApiSuccess> {
    return apiMiddleware.post<ApiSuccess>('firms/', data)
  }

  // Update a firm
  async updateFirm(id: string, data: Partial<CreateFirmData>): Promise<ApiSuccess & { firm: Firm }> {
    return apiMiddleware.put<ApiSuccess & { firm: Firm }>(`firms/${id}/`, data)
  }

  // Delete a firm
  async deleteFirm(id: string): Promise<ApiSuccess> {
    return apiMiddleware.delete<ApiSuccess>(`firms/${id}/`)
  }

  // Check email availability for firms
  async checkFirmEmailAvailability(email: string, excludeFirmId?: string): Promise<EmailAvailabilityResponse> {
    const data: any = { email }
    if (excludeFirmId) {
      data.exclude_firm_id = excludeFirmId
    }
    
    return apiMiddleware.post<EmailAvailabilityResponse>('firms/check-email/', data)
  }
}

export const firmsApi = new FirmsApiService() 